// The page half of the auth stack: `@bindery/auth-consumer/browser`.
//
// DOM only. No worker imports, no `ConsumerAppConfig`, no broker origin, and —
// the point of the split — no `document.cookie` write. Every function here is
// a same-origin call to a route the worker owns, so the page cannot disagree
// with the worker about which broker sign-in leaves for. It used to: `signIn`
// minted the nonce, wrote the state cookie itself, and redirected to a broker
// origin the bundle had picked, while the worker exchanged the returned code
// against whatever its vars said. `/auth/sign-in` is that hop now.
//
// The SPA stores NOTHING: the session JWT lives in the `/auth`-scoped httpOnly
// cookie the worker set, and page JS only ever holds a copy in memory.

import {
  fetchTokenResult as fetchTokenResultAt,
  type TokenFetch,
  type TokenResult,
} from "./token";

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

/** The page-side calls, bound to one consumer's route prefix. */
export interface BrowserAuth {
  /**
   * Begin sign-in. The worker mints the sign-in CSRF nonce, sets the state
   * cookie, and redirects into the broker with this origin's callback as the
   * return target; the broker appends a one-time `?code=` the callback
   * exchanges.
   *
   * `returnTo` is a path — the worker rejects anything that could choose a
   * host — and defaults to where the reader already is.
   */
  signIn: (
    returnTo?: string,
    reader?: { readerId?: string; includeReaders?: boolean },
  ) => void;
  /**
   * Begin the broker's light Google lane, which ends in a `reader_session`.
   * Served only by a consumer that set `readerLinking`. `select` asks for the
   * reader picker even when the identity would go straight through. A
   * `readerId` the worker cannot accept is a 400, never a sign-in without it.
   */
  signInLight: (options?: {
    readerId?: string;
    select?: boolean;
    returnTo?: string;
  }) => void;
  /**
   * Open the broker's management page for `readerId`. Served only by a
   * consumer that set `readerLinking`; an adult not yet linked to the reader
   * is asked to confirm the link and comes back here signed in.
   */
  manageReader: (readerId: string, returnTo?: string) => void;
  /**
   * Sign out via the worker's sign-out route, which clears the session cookie
   * and bounces through the broker sign-out so the upstream session dies too
   * (otherwise the next silent sign-in would sign the user straight back in).
   */
  signOut: (returnTo?: string) => void;
  /**
   * The session token, in memory only, from the worker's token route. No
   * cookie / expired → 401 → `null`, and the caller renders the signed-out
   * state.
   */
  fetchToken: () => Promise<string | null>;
  /** Like `fetchToken`, but tells "signed out" apart from "unreachable". */
  fetchTokenResult: (fetcher?: TokenFetch) => Promise<TokenResult>;
}

/**
 * Bind the page-side calls to the prefix the worker mounted its consumer
 * under — the same `basePath` its `ConsumerAppConfig` names.
 */
export function createBrowserAuth(basePath = "/auth"): BrowserAuth {
  const route = (name: string): string => `${basePath}/${name}`;
  return {
    signIn: (returnTo = currentPath(), reader = {}) => {
      const fields = {
        returnTo,
        readerId: reader.readerId,
        includeReaders: reader.includeReaders === true ? "true" : undefined,
      };
      if (reader.readerId === undefined) {
        window.location.assign(
          `${route("sign-in")}?${queryOf(fields).toString()}`,
        );
      } else {
        postNavigation(route("sign-in"), fields);
      }
    },
    signInLight: (options = {}) => {
      postNavigation(route("sign-in-light"), {
        returnTo: options.returnTo ?? currentPath(),
        readerId: options.readerId,
        select: options.select === true ? "true" : undefined,
      });
    },
    manageReader: (readerId, returnTo = currentPath()) => {
      postNavigation(route("manage"), { returnTo, readerId });
    },
    signOut: (returnTo = currentPath()) => {
      window.location.assign(
        `${route("sign-out")}?returnTo=${encodeURIComponent(returnTo)}`,
      );
    },
    fetchToken: () => fetchTokenAt(route("token")),
    fetchTokenResult: (fetcher) => fetchTokenResultAt(fetcher, route("token")),
  };
}

type Fields = Record<string, string | undefined>;

function queryOf(fields: Fields): URLSearchParams {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(fields)) {
    if (value !== undefined) query.set(name, value);
  }
  return query;
}

// A form POST rather than a GET because Safari before 16.4 sends no
// `Sec-Fetch-Site`, and `Origin` on a form POST is the only proof it gives
// the worker that this page, not another site, chose the reader.
function postNavigation(action: string, fields: Fields): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  form.hidden = true;
  for (const [name, value] of queryOf(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.append(input);
  }
  document.body.append(form);
  form.submit();
}

const defaultAuth = createBrowserAuth();

export const {
  signIn,
  signInLight,
  manageReader,
  signOut,
  fetchToken,
  fetchTokenResult,
} = defaultAuth;

/**
 * Read and strip a `?auth_error=` query param left by a failed
 * `/auth/callback`, or `null` if none is present. Call once at boot and
 * surface the result (e.g. a toast); the param must not linger in the URL bar
 * or survive a refresh.
 */
export function consumeAuthErrorParam(): string | null {
  const url = new URL(window.location.href);
  const authError = url.searchParams.get("auth_error");
  if (authError === null) return null;
  url.searchParams.delete("auth_error");
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  return authError;
}

export {
  TOKEN_FETCH_TIMEOUT_MS,
  type TokenFetch,
  type TokenResult,
} from "./token";

async function fetchTokenAt(path: string): Promise<string | null> {
  try {
    const res = await fetch(path, { credentials: "same-origin" });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (
      typeof body === "object" &&
      body !== null &&
      "token" in body &&
      typeof body.token === "string"
    ) {
      return (body as { token: string }).token;
    }
    return null;
  } catch {
    return null;
  }
}
